import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  MenuItem,
  TextField,
  Switch,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { toast } from "react-toastify";
import axios from "../login/Axios-interceptor";
import {
  MANAGER_COLORS,
  pageShellSx,
  pageInnerSx,
  headerPaperSx,
  toolbarPaperSx,
  contentPaperSx,
  primaryButtonSx,
  dragListContainerSx,
  featureRowSx,
  helperCaptionSx,
} from "../doctor-console-manager/managerTheme.js";
import { fetchCaseSheetAccess, saveCaseSheetAccess } from "./api/caseSheetAccessApi";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

const CaseSheetAccessManager = () => {
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [caseSheets, setCaseSheets] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("accessToken") || "";
    setLoadingDepartments(true);
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_all_medical_departments/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.status === "Success") {
          setDepartments(res.data.department_list || []);
        } else {
          toast.error("Could not load departments");
        }
      })
      .catch((err) => {
        console.error("Failed to load departments", err);
        toast.error("Could not load departments");
      })
      .finally(() => setLoadingDepartments(false));
  }, []);

  const loadAccess = useCallback((departmentId) => {
    if (!departmentId) return;
    setLoadingAccess(true);
    fetchCaseSheetAccess(departmentId)
      .then((data) => setCaseSheets(data?.case_sheets || []))
      .catch((err) => {
        console.error("Failed to load case sheet access", err);
        toast.error("Could not load case sheet access for this department");
        setCaseSheets([]);
      })
      .finally(() => setLoadingAccess(false));
  }, []);

  useEffect(() => {
    if (selectedDepartmentId) loadAccess(selectedDepartmentId);
  }, [selectedDepartmentId, loadAccess]);

  const toggleCaseSheet = (code) => {
    setCaseSheets((prev) =>
      prev.map((cs) =>
        cs.code === code ? { ...cs, is_visible: !cs.is_visible } : cs,
      ),
    );
  };

  const handleSave = async () => {
    if (!selectedDepartmentId) return;
    setSaving(true);
    try {
      await saveCaseSheetAccess(
        selectedDepartmentId,
        caseSheets.map(({ code, is_visible }) => ({ code, is_visible })),
      );
      toast.success("Case sheet access saved");
      loadAccess(selectedDepartmentId);
    } catch (err) {
      console.error("Failed to save case sheet access", err);
      toast.error("Failed to save case sheet access");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={pageShellSx}>
      <Box sx={pageInnerSx}>
        <Paper elevation={0} sx={headerPaperSx}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ pt: 0.5 }}>
            <Box
              sx={{
                mt: 0.25,
                p: 1,
                borderRadius: 1.5,
                bgcolor: MANAGER_COLORS.accentLight,
                color: MANAGER_COLORS.primary,
                display: "flex",
              }}
            >
              <TuneOutlinedIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: MANAGER_COLORS.primaryDark, mb: 0.5 }}
              >
                Case Sheet Access
              </Typography>
              <Typography variant="body2" sx={{ color: MANAGER_COLORS.textMuted, lineHeight: 1.6 }}>
                Choose a department and control which of the 6 case sheets appear for doctors in
                that department.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={toolbarPaperSx}>
          <TextField
            select
            size="small"
            label="Department"
            value={selectedDepartmentId}
            onChange={(e) => setSelectedDepartmentId(e.target.value)}
            disabled={loadingDepartments}
            sx={{ minWidth: 260 }}
          >
            {departments.map((dept) => (
              <MenuItem key={dept.department_id} value={dept.department_id}>
                {dept.department}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        <Paper elevation={0} sx={contentPaperSx}>
          <Box sx={{ p: { xs: 2, md: 2.5 } }}>
            {!selectedDepartmentId ? (
              <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                Select a department above to manage its case sheet visibility.
              </Alert>
            ) : loadingAccess ? (
              <Stack alignItems="center" justifyContent="center" py={6} spacing={2}>
                <CircularProgress sx={{ color: MANAGER_COLORS.primary }} />
                <Typography variant="body2" color="text.secondary">
                  Loading case sheet access…
                </Typography>
              </Stack>
            ) : (
              <>
                <Typography sx={helperCaptionSx}>
                  Toggle which case sheets are visible for this department.
                </Typography>
                <Box sx={dragListContainerSx}>
                  {caseSheets.map((cs) => (
                    <Box key={cs.code} sx={featureRowSx}>
                      <Switch
                        checked={Boolean(cs.is_visible)}
                        onChange={() => toggleCaseSheet(cs.code)}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: MANAGER_COLORS.primaryDark }}
                      >
                        {cs.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button variant="contained" onClick={handleSave} disabled={saving} sx={primaryButtonSx}>
                    {saving ? <CircularProgress size={20} color="inherit" /> : "Save"}
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CaseSheetAccessManager;
